import React from 'react';
import {
  Box,
  Heading,
  Text,
  Flex,
  HStack,
  VStack,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
} from '@chakra-ui/react';
import { useQuery } from 'react-query';
import { operatorApi } from '../services/api';

const planColor = (p: string) => (p === 'empresarial' ? 'purple' : p === 'profissional' ? 'teal' : p === 'essencial' ? 'brand' : 'gray');

// Cor da margem: verde saudável, laranja apertada, vermelho no prejuízo.
const marginColor = (pct: number | null) =>
  pct == null ? 'gray.400' : pct >= 70 ? 'green.600' : pct >= 40 ? 'orange.500' : 'red.500';

const brl = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso: string | null) => {
  if (!iso) return '—';
  const [d] = iso.split('T');
  const [y, m, day] = (d || '').split('-');
  return y ? `${day}/${m}/${y}` : iso;
};

const Operator = () => {
  const { data, isLoading } = useQuery('operator-tenants', operatorApi.tenants);
  const { data: growth } = useQuery('operator-growth', operatorApi.growth);

  const maxSeries = Math.max(1, ...(growth?.signups_series || []).map((s) => s.count));

  return (
    <Box>
      <Heading size="lg" color="brand.700" mb={1}>
        Painel do Operador
      </Heading>
      <Text color="gray.600" mb={6}>
        Acompanhe os clientes da plataforma: cadastros, trials, conversões e consumo.
      </Text>

      {/* Observabilidade de crescimento (PLG) */}
      {growth && (
        <Box mb={8}>
          <Heading size="sm" color="gray.700" mb={3}>Crescimento</Heading>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={4}>
            {([
              ['Cadastros (total)', `${growth.signups.total}`, 'gray.800'],
              ['Novos (7 dias)', `${growth.signups.last_7d}`, 'green.600'],
              ['Ativaram (subiram doc)', `${growth.funnel.activated}`, 'gray.800'],
              ['Pagantes', `${growth.funnel.paying}`, 'green.600'],
            ] as Array<[string, string, string]>).map(([label, val, color]) => (
              <Box key={label} borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
                <Stat>
                  <StatLabel color="gray.500" fontSize="xs">{label}</StatLabel>
                  <StatNumber fontSize="xl" color={color}>{val}</StatNumber>
                </Stat>
              </Box>
            ))}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {/* Funil */}
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">Funil</Text>
              <VStack align="stretch" spacing={2} fontSize="sm">
                <HStack justify="space-between"><Text>Cadastraram</Text><Text fontWeight="semibold">{growth.funnel.signups}</Text></HStack>
                <HStack justify="space-between"><Text>Ativaram (1º documento)</Text><Text fontWeight="semibold">{growth.funnel.activated}</Text></HStack>
                <HStack justify="space-between"><Text>Assinaram (pagantes)</Text><Text fontWeight="semibold" color="green.600">{growth.funnel.paying}</Text></HStack>
                <HStack justify="space-between" pt={1} borderTopWidth="1px">
                  <Text color="gray.500">Conversão cadastro → pago</Text>
                  <Text fontWeight="semibold">{growth.funnel.signups ? Math.round(growth.funnel.paying / growth.funnel.signups * 100) : 0}%</Text>
                </HStack>
              </VStack>
            </Box>

            {/* E-mails de ciclo de vida */}
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">E-mails automáticos enviados</Text>
              <VStack align="stretch" spacing={2} fontSize="sm">
                <HStack justify="space-between"><Text>Boas-vindas</Text><Text fontWeight="semibold">{growth.emails.welcome}</Text></HStack>
                <HStack justify="space-between"><Text>Ativação</Text><Text fontWeight="semibold">{growth.emails.activation}</Text></HStack>
                <HStack justify="space-between"><Text>Upgrade</Text><Text fontWeight="semibold">{growth.emails.upgrade}</Text></HStack>
                <HStack justify="space-between" pt={1} borderTopWidth="1px">
                  <Text color="gray.500">Total</Text><Text fontWeight="semibold">{growth.emails.total}</Text>
                </HStack>
              </VStack>
            </Box>
          </SimpleGrid>

          {/* Cadastros por dia (14 dias) */}
          {growth.signups_series.length > 0 && (
            <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm" mt={4}>
              <Text fontSize="sm" fontWeight="semibold" mb={3} color="gray.700">Cadastros por dia (14 dias)</Text>
              <HStack align="flex-end" spacing={2} h="80px">
                {growth.signups_series.map((s) => (
                  <VStack key={s.day} flex={1} spacing={1} justify="flex-end" h="100%">
                    <Text fontSize="xs" color="gray.500">{s.count}</Text>
                    <Box w="100%" bg="brand.400" borderRadius="sm" h={`${Math.round(s.count / maxSeries * 60)}px`} minH="2px" />
                    <Text fontSize="9px" color="gray.400">{s.day}</Text>
                  </VStack>
                ))}
              </HStack>
            </Box>
          )}
        </Box>
      )}

      {isLoading || !data ? (
        <Flex justify="center" py={16}>
          <Spinner color="brand.500" />
        </Flex>
      ) : (
        <>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
            {([
              ['Clientes', `${data.metrics.total}`, 'gray.800'],
              ['Pagantes', `${data.metrics.paying}`, 'gray.800'],
              ['MRR (receita)', `R$ ${data.metrics.mrr_brl.toLocaleString('pt-BR')}`, 'green.600'],
              ['Custo variável (IA+armaz.)', `R$ ${data.metrics.variable_cost_brl.toLocaleString('pt-BR')}`, 'orange.600'],
              ['Infra fixa (servidor)', `R$ ${data.metrics.infra_cost_brl.toLocaleString('pt-BR')}`, 'orange.600'],
              ['Lucro bruto', `R$ ${data.metrics.gross_profit_brl.toLocaleString('pt-BR')}`, data.metrics.gross_profit_brl >= 0 ? 'green.600' : 'red.600'],
              ['Margem bruta', data.metrics.gross_margin_pct != null ? `${data.metrics.gross_margin_pct}%` : '—', 'gray.800'],
              ['Gasto IA (mês)', `US$ ${data.metrics.ai_spend_month_usd}`, 'gray.800'],
            ] as Array<[string, string, string]>).map(([label, val, color]) => (
              <Box key={label} borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm">
                <Stat>
                  <StatLabel color="gray.500" fontSize="xs">{label}</StatLabel>
                  <StatNumber fontSize="xl" color={color}>{val}</StatNumber>
                </Stat>
              </Box>
            ))}
          </SimpleGrid>

          <Box borderWidth="1px" borderRadius="lg" bg="white" p={4} shadow="sm" overflowX="auto">
            <Heading size="sm" mb={3} color="gray.700">Clientes</Heading>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Empresa</Th>
                  <Th>Plano</Th>
                  <Th isNumeric>Usuários</Th>
                  <Th isNumeric>Trechos</Th>
                  <Th isNumeric>Receita</Th>
                  <Th isNumeric>Custo</Th>
                  <Th isNumeric>Margem</Th>
                  <Th>Última atividade</Th>
                </Tr>
              </Thead>
              <Tbody>
                {data.tenants.map((t) => {
                  const e = t.economics;
                  return (
                  <Tr key={t.tenant_id}>
                    <Td>{t.name}</Td>
                    <Td><Badge colorScheme={planColor(t.plan)}>{t.plan}</Badge></Td>
                    <Td isNumeric>{t.users}</Td>
                    <Td isNumeric>{(t.chunks ?? 0).toLocaleString('pt-BR')}</Td>
                    <Td isNumeric>{e ? brl(e.revenue_brl) : '—'}</Td>
                    <Td isNumeric>
                      {e ? brl(e.total_cost_brl) : '—'}
                      {e && (
                        <Text fontSize="xs" color="gray.400">IA {brl(e.ai_cost_brl)} · arm. {brl(e.storage_cost_brl)}</Text>
                      )}
                    </Td>
                    <Td isNumeric>
                      <Text fontWeight="semibold" color={marginColor(e?.margin_pct ?? null)}>
                        {e?.margin_pct != null ? `${e.margin_pct}%` : '—'}
                      </Text>
                    </Td>
                    <Td>{fmtDate(t.last_activity)}</Td>
                  </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </>
      )}
    </Box>
  );
};

export default Operator;
